import type { CalibrationBlocker } from '@/api/designs';
import {
  placementMmFromCanvasJson,
  type CanvasObjectJSON,
} from '@designdeck/shared';
import type { PrintContext } from '@/types/printContext';

/** A master position as far as the publish gate reads it. */
interface GatePosition {
  json?: { objects?: unknown[] } | null;
  printContext?: Pick<PrintContext, 'widthPx' | 'heightPx'> | null;
}

/**
 * The position slugs that hold a placement: objects with a bounding box on
 * their area. The server counts a position row only with a valid
 * placementMm (U12), so a position whose objects span nothing is no design
 * here either. The box is read in canvas pixels; the Pixel Scale does not
 * change whether one exists.
 */
export function positionsWithPlacement(
  masterPositions: Readonly<Record<string, GatePosition | undefined>>
): Set<string> {
  const slugs = new Set<string>();
  for (const [slug, position] of Object.entries(masterPositions)) {
    const objects = position?.json?.objects;
    const ctx = position?.printContext;
    if (!Array.isArray(objects) || objects.length === 0 || !ctx) continue;
    const box = placementMmFromCanvasJson(
      { objects: objects as CanvasObjectJSON[] },
      { widthMm: ctx.widthPx, heightMm: ctx.heightPx }
    );
    if (box) slugs.add(slug);
  }
  return slugs;
}

/**
 * The calibration reason that blocks the publish step right now (R13, R31).
 *
 * The server reports every reason for the saved design; the step only
 * counts what the seller has not fixed since: a product the seller
 * deselected no longer blocks, and neither does a position whose motif the
 * seller removed. The server applies the same rule again on publish.
 *
 * @param blockers - As loaded with the design (GET /designs/:id)
 * @param activeProductIds - Products active in the current selection
 * @param positionsWithDesign - Position slugs that currently hold a motif
 * @returns The first blocking reason, products before positions; null when none
 */
export function activeCalibrationBlocker(
  blockers: readonly CalibrationBlocker[],
  activeProductIds: ReadonlySet<string>,
  positionsWithDesign: ReadonlySet<string>
): CalibrationBlocker | null {
  const open = blockers.filter(
    (blocker) =>
      activeProductIds.has(blocker.productId) &&
      (blocker.code === 'product_uncalibrated' ||
        positionsWithDesign.has(blocker.positionSlug))
  );
  return (
    open.find((blocker) => blocker.code === 'product_uncalibrated') ??
    open[0] ??
    null
  );
}
